//** Import Components */
import { useState } from "react";
import {
  Table,
  Tooltip,
  Empty,
  Space,
  Collapse,
  Grid,
  Button,
  Pagination,
  Tag,
  Modal,
  Form,
  Input,
  message,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment";
import { TABLE_DATE_FORMAT } from "../common/constants";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import axiosWithAuth from "../utils/axiosWithAuth";
import UploadMedia from "../common/FormInputs/UploadMedia";

export const useManageVideosValidate = ({ refreshData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [modalForm] = Form.useForm();
  const navigate = useNavigate();

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { Panel } = Collapse;

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setIsSubmitting(false);
  };

  //* Function to delete a list item
  const deleteListItem = (APIURL) => {
    if (window.confirm("Are you sure you wish to delete this item?")) {
      axiosWithAuth()
        .delete(APIURL)
        .then((res) => {
          console.log(res.data, APIURL);
          message.success("Video deleted successfully");
          // Refresh the data after successful deletion
          if (refreshData) {
            refreshData();
          }
        })
        .catch((err) => {
          console.log(err);
          message.error("Error deleting video");
        });
    }
  };

  //* Function to upload image and get URL
  const handleImageUpload = async (file, businessId) => {
    const uploadURL = `${process.env.REACT_APP_BE_API_URL}/api/common/uploadImage/businesses-${businessId}`;

    const config = {
      headers: {
        "content-type": "multipart/form-data",
      },
    };

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axiosWithAuth().post(
        `${uploadURL}/file_${new Date().getTime()}`,
        formData,
        config,
      );

      return response.data.url;
    } catch (err) {
      console.log("Error uploading image", err.response);
      throw err;
    }
  };

  //* Function to create new item
  const createListItem = async (values) => {
    const APIURL = `/api/business/mediaBusiness`;
    setIsSubmitting(true);

    try {
      let fileURL = null;

      // Upload file if provided
      if (values.file && values.file instanceof File) {
        fileURL = await handleImageUpload(values.file, values.business_id);
      }

      // Create the listing data
      const listingData = {
        business_id: values.business_id,
        url: values.url,
        name: values.name,
      };

      // Add file URL if it exists
      if (fileURL) {
        listingData.file = fileURL;
      }

      // Send POST request with JSON data
      const res = await axiosWithAuth().post(APIURL, listingData);

      console.log(res.data);

      let config = {
        content: `New video created!`,
        style: {
          background: "none",
        },
      };
      message.success(config);

      closeModal();
      setIsSubmitting(false);
      modalForm.resetFields();
      // Refresh the data after successful creation
      if (refreshData) {
        refreshData();
      }
    } catch (err) {
      console.log(err.response);
      message.error(err.message || "Error creating video");
      setIsSubmitting(false);
    }
  };

  //* functions to handle modal
  const showModal = () => {
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  const submitModalForm = () => {
    setIsSubmitting(true);
    modalForm.submit();
  };

  const dateFormat = TABLE_DATE_FORMAT;

  //* Component that holds the modal
  const modalComponent = (
    <Modal
      open={openModal}
      title="Create New Video to Promote"
      onCancel={closeModal}
      footer={[
        <Button key="back" onClick={closeModal}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={isSubmitting}
          onClick={submitModalForm}
        >
          Submit
        </Button>,
      ]}
    >
      <Form
        form={modalForm}
        layout="vertical"
        name="userForm"
        onFinish={createListItem}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          name="name"
          label="Video to Promote Name"
          rules={[
            {
              required: true,
              message: "Please input a name!",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="url"
          label="Video URL"
          rules={[
            {
              required: true,
              message: "Please input an url!",
            },
            {
              type: "url",
              message: "Please enter a valid URL!",
            },
            {
              pattern: /^https?:\/\/.+/,
              message: "URL must start with http:// or https://",
            },
          ]}
        >
          <Input placeholder="https://example.com" />
        </Form.Item>
        <Form.Item name="file">
          <UploadMedia
            setMedia={(file) =>
              modalForm.setFieldsValue({
                file: file,
              })
            }
            maxCount={1}
            label="Video Cover Image"
            fieldName="file"
            form={modalForm}
            aspectRatio={1.77}
            description="Recommended: 1280x720px (16:9 aspect ratio) - Max Size 10MB"
          />
        </Form.Item>
        <Form.Item name="business_id" hidden>
          <Input />
        </Form.Item>
      </Form>
    </Modal>
  );

  const CreateItemButton = ({ btnText, businessID }) => {
    return (
      <div className="create-item">
        <Button
          type="primary"
          onClick={() => {
            modalForm.setFieldsValue({ business_id: businessID });
            showModal();
          }}
        >
          {btnText}
        </Button>
      </div>
    );
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (text) => (
        <Link to={`${text.id}`} state={{ videoData: text }}>
          {text.name || "No name"}
        </Link>
      ),
    },
    {
      title: "URL",
      key: "url",
      render: (text) => (
        <a href={text.url} target="_blank" rel="noopener noreferrer">
          View video
        </a>
      ),
    },
    {
      title: "Date Created",
      key: "created",
      render: (text) => (
        <>{moment(new Date(text && text.created_at)).format(dateFormat)}</>
      ),
    },
    {
      title: "Status",
      key: "approved",
      render: (text) => (
        <Tag color={text.approved === 1 ? "green" : "orange"}>
          {text.approved === 1 ? "Approved" : "Pending"}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text) => (
        <Space size="middle">
          <button
            onClick={() => {
              const APIURL = `/api/business/mediaBusiness/${text.id}`;
              deleteListItem(APIURL, text.id);
            }}
          >
            <Tooltip title="Delete">
              <FontAwesomeIcon icon={faTrashAlt} />
            </Tooltip>
          </button>
        </Space>
      ),
    },
  ];

  const CardListComponent = ({ listItems }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const start = (currentPage - 1) * pageSize;
    const currentItems = listItems.slice(start, start + pageSize);

    return (
      <>
        <Collapse accordion>
          {currentItems.map((item) => (
            <Panel
              key={item.id}
              header={
                <div>
                  <strong>{item.name || "No name"}</strong>
                </div>
              }
            >
              <p>
                <strong>Name:</strong> {item.name || "No name"}
              </p>
              <p>
                <strong>URL:</strong> {item.url}
              </p>
              <p>
                <strong>Date Created:</strong>{" "}
                {moment(new Date(item && item.created_at)).format(dateFormat)}
              </p>
              <p>
                <strong>Status:</strong>{" "}
                <Tag color={item.approved === 1 ? "green" : "orange"}>
                  {item.approved === 1 ? "Approved" : "Pending"}
                </Tag>
              </p>
              <Button
                onClick={() => {
                  const APIURL = `/api/business/mediaBusiness/${item.id}`;
                  deleteListItem(APIURL, item.id);
                }}
              >
                <Tooltip title="Delete">
                  <FontAwesomeIcon icon={faTrashAlt} />
                </Tooltip>
              </Button>
            </Panel>
          ))}
        </Collapse>

        <div style={{ marginTop: 16, textAlign: "center" }}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={listItems.length}
            onChange={(page) => setCurrentPage(page)}
            size="small"
          />
        </div>
      </>
    );
  };

  const ListComponent = ({ emptyMessage, listItems }) => {
    if (listItems.length === 0) {
      return (
        <Empty
          image="https://gw.alipayobjects.com/zos/antfincdn/ZHrcdLPrvN/empty.svg"
          imageStyle={{ height: 60 }}
          description={<span>{emptyMessage}</span>}
        />
      );
    }

    return screens.md ? (
      <Table columns={columns} dataSource={listItems} />
    ) : (
      <CardListComponent listItems={listItems} />
    );
  };

  return [modalComponent, CreateItemButton, ListComponent];
};
